import secureAxios from "./secureAxios";
import { createBackgroundRequestConfig } from "../hooks/useAuthIntegration";

export { secureAxios };

// ==================== CONFIGURATION ====================
// API Base URL Configuration for Google Cloud
export const getApiBaseURL = () => {
  // Priority: Environment variable > secureAxios default > localhost fallback
  const baseURL =
    process.env.REACT_APP_API_BASE_URL ||
    secureAxios.defaults.baseURL ||
    "http://localhost:5000";

  return baseURL;
};

// ==================== UUID SANITIZATION UTILITY ====================
// Helper function to validate and sanitize UUID values
const sanitizeUUID = (value) => {
  if (!value || value === "" || value === "undefined" || value === "null") {
    return null;
  }

  // Check if it's a valid UUID format
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (typeof value === "string" && uuidRegex.test(value)) {
    return value;
  }

  return null;
};

// ==================== DASHBOARD WIDGETS ====================
// Get user widget preferences - NO userId in URL
export const getUserWidgetPrefs = () => {
  return secureAxios.get(
    "/api/dashboard-widgets/preferences",
    createBackgroundRequestConfig()
  );
};

// Update user widget preferences - NO userId in URL
export const updateUserWidgetPrefs = (widgetData) => {
  // widgetData should contain { widgets: [...] }
  // tenant_id comes from the authenticated user on backend
  return secureAxios.put("/api/dashboard-widgets/preferences", widgetData);
};

// Get available widgets - NO parameters needed
export const getAvailableWidgets = () => {
  return secureAxios.get(
    "/api/dashboard-widgets/available",
    createBackgroundRequestConfig()
  );
};

// ==================== STATUS MANAGEMENT ====================
// Status endpoints
export const statusApi = {
  // Get all statuses
  getAll: () => secureAxios.get("/api/status"),

  // Get status types
  getTypes: () => secureAxios.get("/api/status/types"),

  // Create new status
  create: (data) => secureAxios.post("/api/status", data),

  // Update status
  update: (id, data) => secureAxios.put(`/api/status/${id}`, data),

  // Delete status
  delete: (id) => secureAxios.delete(`/api/status/${id}`),
};

// ==================== USER MANAGEMENT ====================
// User endpoints
export const userApi = {
  // Get all users
  getAll: () => secureAxios.get("/api/users"),

  // Get single user
  getById: (id) => secureAxios.get(`/api/users/${id}`),

  // Create new user
  create: (data) => secureAxios.post("/api/users", data),

  // Update user
  update: (id, data) => secureAxios.put(`/api/users/${id}`, data),

  // Delete user
  delete: (id) => secureAxios.delete(`/api/users/${id}`),

  // Get user profile (current user)
  getProfile: () => secureAxios.get("/api/users/profile"),

  // Update profile (current user)
  updateProfile: (data) => secureAxios.put("/api/users/profile", data),
};

// ==================== AUTHENTICATION ====================
// Auth endpoints with enhanced security for HIPAA
export const authApi = {
  // Login with PHI access acknowledgment
  login: (credentials) => secureAxios.post("/api/auth/login", credentials),

  // Secure logout with session cleanup
  logout: () => secureAxios.post("/api/auth/logout"),

  // Register with HIPAA compliance requirements
  register: (data) => secureAxios.post("/api/auth/register", data),

  // Get current user profile
  getProfile: () => secureAxios.get("/api/auth/profile"),

  // Refresh token for session management
  refreshToken: () => secureAxios.post("/api/auth/refresh"),

  // Forgot password with audit logging
  forgotPassword: (email) =>
    secureAxios.post("/api/auth/forgot-password", { email }),

  // Reset password with security validation
  resetPassword: (token, password) =>
    secureAxios.post("/api/auth/reset-password", { token, password }),

  // Setup initial password (for invited users)
  setupPassword: (userId, password) =>
    secureAxios.post("/api/auth/setup-password", { user_id: userId, password }),

  // Verify email for account activation
  verifyEmail: (token) => secureAxios.post("/api/auth/verify-email", { token }),
};

// ==================== TENANT MANAGEMENT ====================
// Tenant endpoints for multi-tenant HIPAA compliance
export const tenantApi = {
  // Get all tenants (for is_primary users)
  getAll: () => secureAxios.get("/api/tenants"),

  // Get current tenant info
  getCurrent: () => secureAxios.get("/api/tenant/current"),

  // Update tenant settings
  updateSettings: (data) => secureAxios.put("/api/tenant/settings", data),

  // Get tenant users
  getUsers: () => secureAxios.get("/api/tenant/users"),

  // Invite user to tenant with role assignment
  inviteUser: (email, role) =>
    secureAxios.post("/api/tenant/invite", { email, role }),

  // Get tenant HIPAA compliance status
  getComplianceStatus: () => secureAxios.get("/api/tenant/hipaa-status"),
};

// ==================== AUDIT LOGS (HIPAA CRITICAL) ====================
// Enhanced audit log endpoints for HIPAA compliance
export const auditApi = {
  // Get audit logs with filters
  getLogs: (params) => secureAxios.get("/api/audit-logs", { params }),

  // Get specific log entry
  getLogById: (id) => secureAxios.get(`/api/audit-logs/${id}`),

  // Export audit logs for compliance reporting
  exportLogs: (params) =>
    secureAxios.get("/api/audit-logs/export", {
      params,
      responseType: "blob",
    }),

  // Get filter options (distinct values)
  getFilterOptions: () => secureAxios.get("/api/audit-logs/filter-options"),

  // Log an audit entry - HIPAA compliant with enhanced validation and UUID sanitization
  logEntry: async (data) => {
    try {
      // Validate required fields for HIPAA compliance
      if (!data || !data.action || !data.entity_type) {
        return { success: false, reason: "missing_required_fields" };
      }

      // Enhanced validation for PHI-related actions
      if (data.contains_phi && !data.purpose_of_use) {
        return { success: false, reason: "missing_phi_purpose" };
      }

      // Skip auth-related audit logs from frontend (security requirement)
      const authActions = [
        "LOGIN_SUCCESS",
        "LOGIN_FAILED",
        "LOGOUT",
        "SESSION_TIMEOUT",
      ];
      if (data.entity_type === "AUTH" && authActions.includes(data.action)) {
        return { success: false, reason: "auth_backend_only" };
      }

      // Sanitize UUID fields before sending
      const sanitizedEntityId = sanitizeUUID(data.entity_id);
      const sanitizedUserId = sanitizeUUID(data.user_id);
      const sanitizedTenantId = sanitizeUUID(data.tenant_id);
      const sanitizedConsentId = sanitizeUUID(data.consent_id);

      // Add HIPAA compliance metadata with sanitized UUIDs
      const auditData = {
        ...data,
        entity_id: sanitizedEntityId,
        user_id: sanitizedUserId,
        tenant_id: sanitizedTenantId,
        consent_id: sanitizedConsentId,
        timestamp: data.timestamp || new Date().toISOString(),
        compliance_framework: "HIPAA",
        data_classification: data.contains_phi ? "PHI" : "non-PHI",
      };

      // Remove any undefined fields to avoid sending them
      Object.keys(auditData).forEach((key) => {
        if (auditData[key] === undefined) {
          delete auditData[key];
        }
      });

      // Attempt to log the entry
      const response = await secureAxios.post("/api/audit-logs/log", auditData);
      return { success: true, data: response.data };
    } catch (error) {
      // HIPAA Requirement: Never let audit failures break healthcare workflows
      // For HIPAA compliance, we should track audit failures
      try {
        await secureAxios.post("/api/audit-logs/log", {
          action: "AUDIT_LOG_FAILURE",
          entity_type: "SYSTEM",
          action_status: "FAILED",
          entity_identifier: "Audit logging system failure",
          new_values: {
            original_action: data?.action,
            original_entity_type: data?.entity_type,
            failure_reason: error.message,
            failure_status: error.response?.status,
          },
          compliance_framework: "HIPAA",
          data_classification: "system",
        });
      } catch (secondaryError) {
        // Secondary failure - critical for compliance
      }

      return {
        success: false,
        reason: "api_error",
        error: error.message,
        requiresAttention: true, // Flag for HIPAA compliance team
      };
    }
  },

  // Safe wrapper for logging PHI access actions
  logPHIAccess: async (action, entityType, entityId, purpose, details = {}) => {
    return auditApi.logEntry({
      action,
      entity_type: entityType,
      entity_id: entityId, // Will be sanitized in logEntry
      purpose_of_use: purpose,
      contains_phi: true,
      data_sensitivity_level: "high",
      entity_identifier:
        details.description || `PHI ${action} on ${entityType}`,
      action_reason: details.reason,
      fields_accessed: details.fields || [],
      phi_types: details.phi_types || ["medical_information"],
      new_values: details,
      timestamp: new Date().toISOString(),
    });
  },

  // Safe wrapper for general actions
  logActionSafe: async (action, entityType, entityId, details = {}) => {
    return auditApi.logEntry({
      action,
      entity_type: entityType,
      entity_id: entityId, // Will be sanitized in logEntry
      entity_identifier: details.description || `${action} on ${entityType}`,
      action_status: details.status || "SUCCESS",
      action_reason: details.reason,
      contains_phi: details.contains_phi || false,
      data_sensitivity_level: details.sensitivity || "medium",
      new_values: details,
      timestamp: new Date().toISOString(),
    });
  },

  // Export the UUID sanitization function for use elsewhere
  sanitizeUUID: sanitizeUUID,
};

// ==================== ROLE MANAGEMENT ====================
// Role endpoints with HIPAA access controls
export const roleApi = {
  // Get all roles
  getAll: () => secureAxios.get("/api/roles"),

  // Get permissions with HIPAA constraints
  getPermissions: () => secureAxios.get("/api/roles/permissions"),

  // Create role with audit logging
  create: (data) => secureAxios.post("/api/roles", data),

  // Update role with change tracking
  update: (id, data) => secureAxios.put(`/api/roles/${id}`, data),

  // Delete role with audit requirements
  delete: (id) => secureAxios.delete(`/api/roles/${id}`),

  // Get HIPAA-specific role permissions
  getHIPAAPermissions: () => secureAxios.get("/api/roles/hipaa-permissions"),
};

// ==================== PHI ACKNOWLEDGMENT MANAGEMENT (HIPAA CRITICAL) ====================
// PHI Acknowledgment endpoints for HIPAA compliance
export const phiAcknowledgmentApi = {
  // Check if user has valid PHI acknowledgment
  check: (moduleType = "consumers") =>
    secureAxios.get("/api/phi-acknowledgments/check", {
      params: { module_type: moduleType },
    }),

  // Record new PHI acknowledgment with enhanced validation
  record: async (data) => {
    try {
      const acknowledgmentData = {
        module_type: data.moduleType || "consumers",
        purpose: data.purpose || "treatment",
        reason: data.reason || "",
        emergency_access: data.emergencyAccess || false,
        validity_days: data.validityDays || 90,
        acknowledgment_text: data.acknowledgmentText || "",
        timestamp: new Date().toISOString(),
      };

      const response = await secureAxios.post(
        "/api/phi-acknowledgments/record",
        acknowledgmentData
      );

      // Log the acknowledgment for audit trail
      await auditApi.logEntry({
        action: "PHI_ACKNOWLEDGMENT_RECORDED",
        entity_type: "PHI_ACCESS",
        entity_identifier: `PHI acknowledgment for ${acknowledgmentData.module_type}`,
        new_values: {
          module_type: acknowledgmentData.module_type,
          purpose: acknowledgmentData.purpose,
          validity_days: acknowledgmentData.validity_days,
        },
        compliance_framework: "HIPAA",
        data_classification: "access-control",
      });

      return response;
    } catch (error) {
      throw error;
    }
  },

  // Get user's acknowledgment history
  getHistory: () => secureAxios.get("/api/phi-acknowledgments/history"),

  // Check multiple modules at once
  checkMultiple: (moduleTypes) =>
    secureAxios.post("/api/phi-acknowledgments/check-multiple", {
      module_types: moduleTypes,
    }),

  // Revoke acknowledgment (for security incidents)
  revoke: (acknowledgmentId, reason) =>
    secureAxios.post(`/api/phi-acknowledgments/${acknowledgmentId}/revoke`, {
      reason,
    }),
};

// ==================== CONSUMER/PATIENT MANAGEMENT (PHI) ====================
// Consumer endpoints with HIPAA protections
export const consumerApi = {
  // Get all consumers (requires PHI acknowledgment)
  getAll: (params) => secureAxios.get("/api/consumers", { params }),

  // Get single consumer (PHI access)
  getById: (id) => secureAxios.get(`/api/consumers/${id}`),

  // Create new consumer (PHI creation)
  create: (data) => secureAxios.post("/api/consumers", data),

  // Update consumer (PHI modification)
  update: (id, data) => secureAxios.put(`/api/consumers/${id}`, data),

  // Delete consumer (requires special authorization)
  delete: (id) => secureAxios.delete(`/api/consumers/${id}`),

  // Search consumers with audit logging
  search: (query) =>
    secureAxios.get("/api/consumers/search", {
      params: { q: query },
    }),
};

// ==================== PLANS/TREATMENT RECORDS (PHI) ====================
// Plan endpoints for treatment data
export const planApi = {
  // Get all plans for a consumer
  getByConsumer: (consumerId) =>
    secureAxios.get(`/api/plans/consumer/${consumerId}`),

  // Get single plan
  getById: (id) => secureAxios.get(`/api/plans/${id}`),

  // Create new plan
  create: (data) => secureAxios.post("/api/plans", data),

  // Update plan
  update: (id, data) => secureAxios.put(`/api/plans/${id}`, data),

  // Delete plan
  delete: (id) => secureAxios.delete(`/api/plans/${id}`),
};

// ==================== CHAT MESSAGING (HIPAA COMPLIANT) ====================
// Chat endpoints for internal messaging between users
// PHASE 5: Added search, reactions, enhanced read receipts
export const chatApi = {
  // ==================== CONVERSATIONS ====================

  /**
   * Get all conversations for the current user
   * @returns {Promise} List of conversations with other participant info
   */
  getConversations: () => secureAxios.get("/api/chat/conversations"),

  /**
   * Start a new conversation with another user
   * @param {Object} data - { recipient_id, authorization_id?, consumer_id? }
   * @returns {Promise} New conversation details
   */
  createConversation: (data) =>
    secureAxios.post("/api/chat/conversations", data),

  /**
   * Get a single conversation's details
   * @param {string} conversationId - UUID of the conversation
   * @returns {Promise} Conversation details
   */
  getConversation: (conversationId) =>
    secureAxios.get(`/api/chat/conversations/${conversationId}`),

  /**
   * Get messages in a conversation with pagination
   * Phase 5: Now includes reactions and delivered_at/read_at for enhanced receipts
   * @param {string} conversationId - UUID of the conversation
   * @param {Object} params - { limit?, before? } for pagination
   * @returns {Promise} List of messages with reactions
   */
  getMessages: (conversationId, params = {}) =>
    secureAxios.get(`/api/chat/conversations/${conversationId}/messages`, {
      params,
    }),

  /**
   * Mark all messages in a conversation as read
   * @param {string} conversationId - UUID of the conversation
   * @returns {Promise} Success response
   */
  markConversationRead: (conversationId) =>
    secureAxios.put(`/api/chat/conversations/${conversationId}/read-all`),

  // ==================== MESSAGES ====================

  /**
   * Send a new message
   * @param {Object} data - { recipient_id, message, authorization_id?, consumer_id? }
   * @returns {Promise} Sent message with conversation_id
   */
  sendMessage: (data) => secureAxios.post("/api/chat/messages", data),

  /**
   * Edit an existing message (sender only)
   * @param {string} messageId - UUID of the message
   * @param {Object} data - { message: newContent }
   * @returns {Promise} Updated message
   */
  editMessage: (messageId, data) =>
    secureAxios.put(`/api/chat/messages/${messageId}`, data),

  /**
   * Delete a message (soft delete - HIPAA compliant)
   * Preserves audit trail, hides content from UI
   * @param {string} messageId - UUID of the message
   * @returns {Promise} Success response with deleted_at timestamp
   */
  deleteMessage: (messageId) =>
    secureAxios.delete(`/api/chat/messages/${messageId}`),

  /**
   * Get edit history for a message
   * @param {string} messageId - UUID of the message
   * @returns {Promise} Edit history with original and all versions
   */
  getMessageHistory: (messageId) =>
    secureAxios.get(`/api/chat/messages/${messageId}/history`),

  /**
   * Mark a single message as read
   * @param {string} messageId - UUID of the message
   * @returns {Promise} Success response
   */
  markMessageRead: (messageId) =>
    secureAxios.put(`/api/chat/messages/${messageId}/read`),

  // ==================== GROUP CHAT API ADDITIONS ====================
  // Add these to your existing chatApi object in api.js
  // Place after the existing chat methods

  // ==================== PHASE 6: GROUP CHAT ====================

  /**
   * Create a new group conversation
   * @param {Object} data - { group_name, group_description?, participant_ids[], authorization_id?, consumer_id? }
   * @returns {Promise} New group conversation details
   */
  createGroup: (data) => secureAxios.post("/api/chat/groups", data),

  /**
   * Get group details including participants
   * @param {string} groupId - UUID of the group conversation
   * @returns {Promise} Group details with participants
   */
  getGroup: (groupId) => secureAxios.get(`/api/chat/groups/${groupId}`),

  /**
   * Update group settings (name, description)
   * Requires admin/owner role
   * @param {string} groupId - UUID of the group conversation
   * @param {Object} data - { group_name?, group_description? }
   * @returns {Promise} Updated group details
   */
  updateGroup: (groupId, data) =>
    secureAxios.put(`/api/chat/groups/${groupId}`, data),

  /**
   * Get list of group participants
   * @param {string} groupId - UUID of the group conversation
   * @returns {Promise} { participants: [], count: number }
   */
  getGroupParticipants: (groupId) =>
    secureAxios.get(`/api/chat/groups/${groupId}/participants`),

  /**
   * Add participants to a group
   * Requires admin/owner role
   * @param {string} groupId - UUID of the group conversation
   * @param {Object} data - { participant_ids: [] }
   * @returns {Promise} { success: true, added: [], new_count: number }
   */
  addGroupParticipants: (groupId, data) =>
    secureAxios.post(`/api/chat/groups/${groupId}/participants`, data),

  /**
   * Remove a participant from a group
   * Requires admin/owner role (or self-removal)
   * @param {string} groupId - UUID of the group conversation
   * @param {string} userId - UUID of the user to remove
   * @param {Object} data - { reason?: string }
   * @returns {Promise} { success: true, removed_user_id, is_self_removal }
   */
  removeGroupParticipant: (groupId, userId, data = {}) =>
    secureAxios.delete(`/api/chat/groups/${groupId}/participants/${userId}`, {
      data,
    }),

  /**
   * Update a participant's role (make admin or member)
   * Requires owner role
   * @param {string} groupId - UUID of the group conversation
   * @param {string} userId - UUID of the user
   * @param {Object} data - { new_role: 'admin' | 'member' }
   * @returns {Promise} { success: true, user_id, new_role }
   */
  updateGroupParticipantRole: (groupId, userId, data) =>
    secureAxios.put(
      `/api/chat/groups/${groupId}/participants/${userId}/role`,
      data
    ),

  /**
   * Leave a group (convenience method)
   * @param {string} groupId - UUID of the group conversation
   * @returns {Promise} { success: true }
   */
  leaveGroup: (groupId) =>
    secureAxios.post(`/api/chat/groups/${groupId}/leave`),

  /**
   * Mute/unmute a group
   * @param {string} groupId - UUID of the group conversation
   * @param {Object} data - { muted: boolean, muted_until?: ISO date string }
   * @returns {Promise} { success: true, is_muted }
   */
  toggleGroupMute: (groupId, data) =>
    secureAxios.put(`/api/chat/groups/${groupId}/mute`, data),

  // ==================== UTILITIES ====================

  /**
   * Get total unread message count for badge display
   * @returns {Promise} { unread_count: number }
   */
  getUnreadCount: () => secureAxios.get("/api/chat/unread-count"),

  /**
   * Long-poll for new messages
   * Holds connection until new messages arrive or timeout (25 seconds)
   * @param {Object} params - { since?: ISO timestamp }
   * @param {Object} config - Axios config (for abort signal)
   * @returns {Promise} { messages: [], serverTime, timeout: boolean }
   */
  longPoll: (params = {}, config = {}) =>
    secureAxios.get("/api/chat/poll", {
      params,
      timeout: 30000, // 30 second timeout (slightly longer than server)
      ...config,
    }),

  /**
   * Get users available to chat with (same tenant)
   * @param {Object} params - { search?: string }
   * @returns {Promise} List of users
   */
  getUsers: (params = {}) => secureAxios.get("/api/chat/users", { params }),

  // ==================== PHASE 5: SEARCH ====================

  /**
   * Search through chat message history
   * Can search all conversations or within a specific conversation
   * @param {Object} params - { q: searchQuery, conversation_id?, limit?, offset? }
   * @returns {Promise} { messages: [], total: number, hasMore: boolean }
   */
  searchMessages: (params) => secureAxios.get("/api/chat/search", { params }),

  // ==================== PHASE 5: REACTIONS ====================

  /**
   * Get all reactions for a message
   * @param {string} messageId - UUID of the message
   * @returns {Promise} { reactions: [{ emoji, count, users: [{user_id, user_name}] }] }
   */
  getReactions: (messageId) =>
    secureAxios.get(`/api/chat/messages/${messageId}/reactions`),

  /**
   * Add a reaction to a message
   * @param {string} messageId - UUID of the message
   * @param {string} emoji - The emoji to add (e.g., "ðŸ‘")
   * @returns {Promise} { success: true, reactions: [...] }
   */
  addReaction: (messageId, emoji) =>
    secureAxios.post(`/api/chat/messages/${messageId}/reactions`, { emoji }),

  /**
   * Remove a reaction from a message
   * @param {string} messageId - UUID of the message
   * @param {string} emoji - The emoji to remove
   * @returns {Promise} { success: true, reactions: [...] }
   */
  removeReaction: (messageId, emoji) =>
    secureAxios.delete(
      `/api/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`
    ),
};

// ==================== HELPER FUNCTIONS ====================
// Build full API URL (for cases where you need the full URL)
export const buildApiUrl = (endpoint) => {
  const baseURL = getApiBaseURL();
  return `${baseURL}${endpoint}`;
};

// HIPAA-compliant API call wrapper
export const hipaaApiCall = async (apiFunction, ...args) => {
  try {
    const response = await apiFunction(...args);
    return { success: true, data: response.data };
  } catch (error) {
    const errorInfo = handleApiError(error);

    // Log API failures for HIPAA audit trail
    if (errorInfo.status >= 400) {
      await auditApi
        .logEntry({
          action: "API_CALL_FAILED",
          entity_type: "SYSTEM",
          action_status: "FAILED",
          entity_identifier: `API failure: ${error.config?.method?.toUpperCase()} ${
            error.config?.url
          }`,
          new_values: {
            endpoint: error.config?.url,
            method: error.config?.method,
            status: errorInfo.status,
            message: errorInfo.message,
          },
          compliance_framework: "HIPAA",
        })
        .catch(() => {
          // Silent fail - don't let audit logging break the app
        });
    }

    return { success: false, error: errorInfo };
  }
};

// ==================== API ENDPOINT REFERENCE ====================
// API endpoint reference (for documentation and compliance mapping)
export const apiEndpoints = {
  auth: {
    login: "/api/auth/login",
    logout: "/api/auth/logout",
    register: "/api/auth/register",
    profile: "/api/auth/profile",
    refresh: "/api/auth/refresh",
    setupPassword: "/api/auth/setup-password",
  },
  status: {
    list: "/api/status",
    types: "/api/status/types",
    create: "/api/status",
    update: (id) => `/api/status/${id}`,
    delete: (id) => `/api/status/${id}`,
  },
  users: {
    list: "/api/users",
    profile: "/api/users/profile",
    create: "/api/users",
    update: (id) => `/api/users/${id}`,
    delete: (id) => `/api/users/${id}`,
  },
  consumers: {
    list: "/api/consumers",
    create: "/api/consumers",
    get: (id) => `/api/consumers/${id}`,
    update: (id) => `/api/consumers/${id}`,
    delete: (id) => `/api/consumers/${id}`,
    search: "/api/consumers/search",
  },
  plans: {
    list: "/api/plans",
    create: "/api/plans",
    get: (id) => `/api/plans/${id}`,
    update: (id) => `/api/plans/${id}`,
    delete: (id) => `/api/plans/${id}`,
    byConsumer: (consumerId) => `/api/plans/consumer/${consumerId}`,
  },
  dashboard: {
    preferences: "/api/dashboard-widgets/preferences",
    available: "/api/dashboard-widgets/available",
  },
  audit: {
    logs: "/api/audit-logs",
    log: "/api/audit-logs/log",
    export: "/api/audit-logs/export",
    filterOptions: "/api/audit-logs/filter-options",
  },
  phiAcknowledgments: {
    check: "/api/phi-acknowledgments/check",
    record: "/api/phi-acknowledgments/record",
    history: "/api/phi-acknowledgments/history",
    checkMultiple: "/api/phi-acknowledgments/check-multiple",
    revoke: (id) => `/api/phi-acknowledgments/${id}/revoke`,
  },
  tenant: {
    current: "/api/tenant/current",
    settings: "/api/tenant/settings",
    users: "/api/tenant/users",
    invite: "/api/tenant/invite",
    hipaaStatus: "/api/tenant/hipaa-status",
  },
  chat: {
    // Conversations
    conversations: "/api/chat/conversations",
    conversation: (id) => `/api/chat/conversations/${id}`,
    messages: (conversationId) =>
      `/api/chat/conversations/${conversationId}/messages`,
    markConversationRead: (conversationId) =>
      `/api/chat/conversations/${conversationId}/read-all`,
    // Messages
    sendMessage: "/api/chat/messages",
    editMessage: (id) => `/api/chat/messages/${id}`,
    deleteMessage: (id) => `/api/chat/messages/${id}`,
    messageHistory: (id) => `/api/chat/messages/${id}/history`,
    markMessageRead: (id) => `/api/chat/messages/${id}/read`,
    // Utilities
    unreadCount: "/api/chat/unread-count",
    poll: "/api/chat/poll",
    users: "/api/chat/users",
    // Phase 5: Search
    search: "/api/chat/search",
    // Phase 5: Reactions
    reactions: (messageId) => `/api/chat/messages/${messageId}/reactions`,
    removeReaction: (messageId, emoji) =>
      `/api/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
  },
};

// ==================== ERROR HANDLING ====================
// Enhanced error handler for HIPAA compliance
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error
    const message =
      error.response.data?.error ||
      error.response.data?.message ||
      "An error occurred";
    const status = error.response.status;

    return {
      message,
      status,
      data: error.response.data,
    };
  } else if (error.request) {
    // Request made but no response
    return {
      message: "No response from server. Please check your connection.",
      status: 0,
    };
  } else {
    // Something else happened
    return {
      message: error.message || "An unexpected error occurred",
      status: 0,
    };
  }
};

// Safe API call wrapper - prevents errors from breaking the app
export const safeApiCall = async (apiFunction, ...args) => {
  try {
    const response = await apiFunction(...args);
    return { success: true, data: response.data };
  } catch (error) {
    const errorInfo = handleApiError(error);
    return { success: false, error: errorInfo };
  }
};

// ==================== EXPORTS ====================
// Export everything as default for convenience
export default {
  getApiBaseURL,
  buildApiUrl,
  apiEndpoints,
  handleApiError,
  safeApiCall,
  hipaaApiCall, // New HIPAA-specific wrapper
  sanitizeUUID, // Export UUID sanitization utility

  // API collections
  auth: authApi,
  status: statusApi,
  users: userApi,
  consumers: consumerApi,
  plans: planApi,
  tenant: tenantApi,
  audit: auditApi,
  roles: roleApi,
  phiAcknowledgment: phiAcknowledgmentApi,
  chat: chatApi, // Chat messaging API with Phase 5 features

  // Dashboard widgets (existing)
  getUserWidgetPrefs,
  updateUserWidgetPrefs,
  getAvailableWidgets,
};